import { useState, type ReactNode } from 'react';
import { api } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { Page } from '../../app/AppShell.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote } from '../../components/ui.js';
import { useAuth } from '../../lib/auth.js';
import { useCapabilities } from '../../lib/capabilities.js';
import type {
  ModuleCatalogEntry,
  RegistryListResponse,
  RegistryModuleEntry,
} from '../../lib/types.js';

/**
 * The widget-module marketplace (ADR-0015 / ADR-0016). Lists installed modules
 * (system + custom + registry) with install / enable state, the remote registry
 * catalogue, and a custom module upload (single HTML or a .akmod package).
 */
export function ModulesScreen(): ReactNode {
  const toast = useToast();
  const { can } = useAuth();
  const manage = can('modules.manage');
  const modules = useQuery(() => api.modules(), []);
  const caps = useCapabilities();
  // A box has no registry (and refuses the call), so don't ask.
  const registry = useQuery(
    (): Promise<RegistryListResponse> =>
      caps?.moduleRegistry === false
        ? Promise.resolve({ enabled: false, signingConfigured: false, data: [] })
        : api.registryModules(),
    [caps?.moduleRegistry],
  );
  const [busy, setBusy] = useState<string | null>(null);

  function refetchAll(): void {
    modules.refetch();
    registry.refetch();
  }

  async function act(slug: string, fn: () => Promise<unknown>, done: string): Promise<void> {
    setBusy(slug);
    try {
      await fn();
      toast.push(done, 'success');
      refetchAll();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(null);
    }
  }

  if (modules.loading && !modules.data) return <Loading what="modules" />;
  if (modules.error) return <ErrorNote error={modules.error} />;

  const entries = modules.data?.data ?? [];

  return (
    <Page title="Widget modules">
      <p className="muted small" style={{ marginBottom: 16 }}>
        Install extra widget types for the layout editor. Native widgets (image, video, text, web,
        clock…) are always available. See <code>docs/adr/0015</code>.
      </p>

      <div className="stack">
        {entries.map((m) => (
          <ModuleCard
            key={m.slug}
            module={m}
            manage={manage}
            busy={busy === m.slug}
            onInstall={() => act(m.slug, () => api.installModule(m.slug), `Installed ${m.name}`)}
            onUninstall={() =>
              act(m.slug, () => api.uninstallModule(m.slug), `Uninstalled ${m.name}`)
            }
            onToggle={(enabled) =>
              act(
                m.slug,
                () => api.updateModule(m.slug, { enabled }),
                enabled ? `Enabled ${m.name}` : `Disabled ${m.name}`,
              )
            }
            onRefreshData={() =>
              act(m.slug, () => api.refreshModuleData(m.slug), `Refreshed ${m.name} feeds`)
            }
          />
        ))}
        {entries.length === 0 ? <p className="faint small">No modules registered.</p> : null}
      </div>

      {registry.data?.enabled ? (
        <>
          <div style={{ height: 24 }} />
          <Marketplace
            registry={registry.data}
            manage={manage}
            busy={busy}
            onSync={() =>
              act('__registry', () => api.syncRegistry(), 'Registry synced')
            }
            onInstall={(slug) =>
              act(`registry:${slug}`, () => api.installRegistryModule(slug), `Installed ${slug}`)
            }
          />
        </>
      ) : null}

      {manage ? (
        <>
          <div style={{ height: 24 }} />
          <UploadCustomModule onDone={refetchAll} />
        </>
      ) : null}
    </Page>
  );
}

function ModuleCard({
  module: m,
  manage,
  busy,
  onInstall,
  onUninstall,
  onToggle,
  onRefreshData,
}: {
  module: ModuleCatalogEntry;
  manage: boolean;
  busy: boolean;
  onInstall: () => void;
  onUninstall: () => void;
  onToggle: (enabled: boolean) => void;
  onRefreshData: () => void;
}): ReactNode {
  return (
    <div className="card card-pad">
      <div className="row between" style={{ alignItems: 'flex-start' }}>
        <div>
          <strong>{m.name}</strong>{' '}
          <span className="faint small">
            {m.slug} · v{m.version} · {m.kind}
          </span>
          <div className="row" style={{ gap: 6, marginTop: 4 }}>
            <span className="badge">{m.source}</span>
            {m.installed ? (
              <span className={`badge ${m.enabled ? 'ok' : 'warn'}`}>
                {m.enabled ? 'installed' : 'disabled'}
              </span>
            ) : null}
            {m.status !== 'active' ? <span className="badge warn">{m.status}</span> : null}
          </div>
          {m.description ? (
            <p className="muted small" style={{ margin: '6px 0 0' }}>
              {m.description}
            </p>
          ) : null}
          {m.kind === 'data' && m.installed ? <FeedStatus feeds={m.feeds ?? []} /> : null}
        </div>
        {manage ? (
          <div className="row" style={{ gap: 6 }}>
            {!m.installed ? (
              <button className="btn small primary" disabled={busy} onClick={onInstall}>
                Install
              </button>
            ) : (
              <>
                {m.kind === 'data' ? (
                  <button className="btn small" disabled={busy} onClick={onRefreshData}>
                    Refresh now
                  </button>
                ) : null}
                <button className="btn small" disabled={busy} onClick={() => onToggle(!m.enabled)}>
                  {m.enabled ? 'Disable' : 'Enable'}
                </button>
                <button className="btn small ghost danger" disabled={busy} onClick={onUninstall}>
                  Uninstall
                </button>
              </>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FeedStatus({ feeds }: { feeds: NonNullable<ModuleCatalogEntry['feeds']> }): ReactNode {
  if (feeds.length === 0) {
    return (
      <p className="faint small" style={{ margin: '6px 0 0' }}>
        No layouts use this widget yet — nothing to fetch.
      </p>
    );
  }
  return (
    <ul className="small" style={{ margin: '6px 0 0', paddingLeft: 16 }}>
      {feeds.map((f) => (
        <li key={f.configHash} className={f.status === 'error' ? 'form-error' : 'muted'}>
          {f.providerType}: {f.status}
          {f.fetchedAt ? ` · ${new Date(f.fetchedAt).toLocaleString()}` : ''}
          {f.error ? ` — ${f.error}` : ''}
        </li>
      ))}
    </ul>
  );
}

function Marketplace({
  registry,
  manage,
  busy,
  onSync,
  onInstall,
}: {
  registry: RegistryListResponse;
  manage: boolean;
  busy: string | null;
  onSync: () => void;
  onInstall: (slug: string) => void;
}): ReactNode {
  return (
    <div className="card card-pad">
      <div className="row between" style={{ alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Marketplace</h2>
        {manage ? (
          <button className="btn small" disabled={busy === '__registry'} onClick={onSync}>
            Sync registry
          </button>
        ) : null}
      </div>
      {!registry.signingConfigured ? (
        <p className="form-error small" style={{ marginTop: 8 }}>
          No registry public key configured — install is disabled. Set{' '}
          <code>MODULE_REGISTRY_PUBLIC_KEY</code>.
        </p>
      ) : null}
      <div className="stack" style={{ marginTop: 12 }}>
        {registry.data.map((m: RegistryModuleEntry) => (
          <div key={m.slug} className="row between" style={{ alignItems: 'flex-start' }}>
            <div>
              <strong>{m.name}</strong>{' '}
              <span className="faint small">
                {m.slug} · {m.kind} · {Object.entries(m.channels).map(([c, v]) => `${c} ${v}`).join(', ')}
              </span>
              {m.description ? (
                <p className="muted small" style={{ margin: '4px 0 0' }}>
                  {m.description}
                </p>
              ) : null}
            </div>
            {manage ? (
              m.installed ? (
                <span className="badge ok">installed v{m.installedVersion}</span>
              ) : (
                <button
                  className="btn small primary"
                  disabled={!registry.signingConfigured || busy === `registry:${m.slug}`}
                  onClick={() => onInstall(m.slug)}
                >
                  Install
                </button>
              )
            ) : null}
          </div>
        ))}
        {registry.data.length === 0 ? (
          <p className="faint small">Nothing in the registry yet — try Sync.</p>
        ) : null}
      </div>
    </div>
  );
}

function UploadCustomModule({ onDone }: { onDone: () => void }): ReactNode {
  const toast = useToast();
  const [mode, setMode] = useState<'single' | 'package'>('single');
  const [manifest, setManifest] = useState('');
  const [template, setTemplate] = useState<File | null>(null);
  const [pkg, setPkg] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);

  const ready = mode === 'single' ? manifest.trim() !== '' && template !== null : pkg !== null;

  async function submit(): Promise<void> {
    if (!ready) return;
    setBusy(true);
    try {
      const created =
        mode === 'single'
          ? await api.uploadModule(manifest, template as File)
          : await api.uploadModulePackage(pkg as File);
      toast.push(`Uploaded ${created.name}`, 'success');
      setManifest('');
      setTemplate(null);
      setPkg(null);
      onDone();
    } catch (err) {
      toast.error(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card card-pad">
      <h2 style={{ marginTop: 0 }}>Upload a custom module</h2>
      <p className="muted small">
        Installed for this account only. Format: <code>docs/spec/module-manifest.md</code>.
      </p>
      <div className="row" style={{ gap: 12, marginBottom: 8 }}>
        <label className="row small" style={{ gap: 4 }}>
          <input type="radio" checked={mode === 'single'} onChange={() => setMode('single')} /> Single
          HTML
        </label>
        <label className="row small" style={{ gap: 4 }}>
          <input type="radio" checked={mode === 'package'} onChange={() => setMode('package')} />{' '}
          <code>.akmod</code> package
        </label>
      </div>

      {mode === 'single' ? (
        <>
          <label className="field">
            manifest.json
            <textarea
              rows={8}
              value={manifest}
              onChange={(e) => setManifest(e.target.value)}
              placeholder='{ "manifestVersion": 1, "slug": "my-widget", ... }'
              style={{ width: '100%', fontFamily: 'monospace' }}
            />
          </label>
          <label className="field">
            template.html
            <input
              type="file"
              accept=".html,text/html"
              onChange={(e) => setTemplate(e.target.files?.[0] ?? null)}
            />
          </label>
        </>
      ) : (
        <label className="field">
          package (.akmod / .zip — carries its own manifest.json)
          <input
            type="file"
            accept=".akmod,.zip,application/zip"
            onChange={(e) => setPkg(e.target.files?.[0] ?? null)}
          />
        </label>
      )}

      <button className="btn primary" disabled={busy || !ready} onClick={() => void submit()}>
        {busy ? 'Uploading…' : 'Upload module'}
      </button>
    </div>
  );
}
